import { useState } from 'react';
import { ApiError } from '../../api/client';
import type { ClipboardRender, ValidationIssue } from '../../api/types';

/** How long the button stays in its "copied" state before going back to its normal label. */
const CONFIRMATION_MS = 4000;

interface CopyPanelProps {
  /**
   * Names what is still empty, or null when nothing is. Drives both the disabled state and the
   * tooltip, so the button can never be disabled without saying why.
   */
  blockedReason: string | null;

  /**
   * The rendered email, kept ready ahead of the click. Null while it is still being fetched — see
   * the note on `copyRenderedEmail` for why the click cannot afford to wait for it.
   */
  html: string | null;

  /** Warnings from the render that produced `html`. */
  warnings: ValidationIssue[];

  /** Fetches it on demand, for the case where the click lands before the prefetch does. */
  render: () => Promise<ClipboardRender>;

  /** Called before the request, so the form reveals what is missing if the button was pressed anyway. */
  onBlocked: () => void;
}

/**
 * Puts the finished email on the clipboard as HTML, for pasting into an Outlook compose window.
 *
 * The alternative to creating a draft, and the only route for a sender who wants to write something
 * around the email before sending it.
 */
export function CopyPanel({ blockedReason, html, warnings, render, onBlocked }: CopyPanelProps) {
  const [state, setState] = useState<'idle' | 'working' | 'copied'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [copiedWarnings, setCopiedWarnings] = useState<ValidationIssue[]>([]);

  // Which route did the work. Reported because the two do not paste alike, and a "Copied" that could
  // have meant either is what made the last round of this impossible to tell apart.
  const [route, setRoute] = useState<'rendered' | 'markup' | null>(null);

  function finish(via: 'rendered' | 'markup', copiedFrom: ValidationIssue[]) {
    setRoute(via);
    setCopiedWarnings(copiedFrom);
    setState('copied');
    window.setTimeout(() => setState('idle'), CONFIRMATION_MS);
  }

  async function copy() {
    if (blockedReason !== null) {
      onBlocked();
      return;
    }

    setError(null);

    // The ready path, and the reason for the prefetch: everything from here to the clipboard runs
    // without awaiting, so the click's user gesture is still in effect when the copy happens.
    if (html !== null && copyRenderedEmail(html)) {
      finish('rendered', warnings);
      return;
    }

    // Either the prefetch has not landed yet or the browser refused the rendered copy. Fall back to
    // handing the markup over as a string, which is what the clipboard API sanitises — worse, but a
    // working button beats a correct one that does nothing.
    setState('working');

    try {
      const rendered = html === null ? await render() : { html, subject: '', issues: warnings };

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([rendered.html], { type: 'text/html' }),
          'text/plain': new Blob([toPlainText(rendered.html)], { type: 'text/plain' }),
        }),
      ]);

      finish('markup', rendered.issues.filter((i) => i.severity === 'warning'));
    } catch (caught) {
      setState('idle');

      if (caught instanceof ApiError) {
        const fieldMessages = Object.values(caught.fieldErrors).flat();
        setError(fieldMessages.length > 0 ? fieldMessages.join(' ') : caught.message);
        onBlocked();
        return;
      }

      setError(
        caught instanceof Error
          ? `The email was rendered but the browser would not write it to the clipboard: ${caught.message}`
          : 'Could not write to the clipboard.',
      );
    }
  }

  const label =
    state === 'copied' ? 'Copied to clipboard' : state === 'working' ? 'Copying…' : 'Copy to clipboard';

  return (
    <section className="copy" aria-label="Copy the email">
      <h2 className="send__title">Copy it instead</h2>
      <p className="send__hint">
        Puts the email on your clipboard. Paste it into a new Outlook message and it arrives laid out
        as it is in the preview. Outlook on the web keeps the most formatting; the desktop client
        renders through Word, which drops rounded corners.
      </p>

      <button
        type="button"
        className="button button--quiet button--wide"
        disabled={state === 'working'}
        // Not the `disabled` attribute: a disabled button fires no events, so it would have no
        // tooltip and no way to explain itself. It looks disabled and says why on hover instead.
        aria-disabled={blockedReason !== null}
        data-blocked={blockedReason !== null ? 'true' : undefined}
        title={blockedReason ?? 'Copy the email as HTML'}
        onClick={copy}
      >
        {label}
      </button>

      {state === 'copied' && (
        <p className="copy__route" role="status">
          {route === 'rendered'
            ? 'Copied by selecting the rendered email — the route that keeps the most formatting.'
            : 'Copied as markup. Your browser refused the richer route, so formatting may be lost on paste.'}
        </p>
      )}

      {state === 'copied' && copiedWarnings.length > 0 && (
        <div className="copy__warnings" role="status">
          <p>Copied, but check this before you send:</p>
          <ul>
            {copiedWarnings.map((issue, index) => (
              <li key={index}>{issue.message}</li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="send__error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * Copies the email by selecting it in a document that has actually rendered it.
 *
 * `navigator.clipboard.write` looks like the modern way to do this, and it is the wrong one here: it
 * takes the markup as a *string* and sanitises it on the way to the clipboard, so what Outlook
 * receives is not what was handed over. Selecting rendered content and letting the browser serialise
 * the selection is the path browsers actually tune for rich copy — it is what happens when someone
 * selects an email in a browser tab and presses Ctrl+C, and that pastes into Outlook well.
 *
 * The email is rendered in an off-screen iframe rather than in this page: it is a whole document with
 * its own `<body>`, and its markup must not meet this application's stylesheet on the way past. The
 * frame is positioned off-screen rather than hidden, because a selection needs laid-out content and
 * `display:none` has none.
 *
 * Synchronous throughout. `execCommand('copy')` only works while the click that started it is still
 * in effect, so there is nothing to await here and the HTML has to arrive already fetched.
 *
 * Returns false when the browser declines, leaving the caller to try the string route.
 */
function copyRenderedEmail(html: string): boolean {
  const returnFocusTo = document.activeElement;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.style.cssText = 'position:fixed; left:-10000px; top:0; width:900px; height:600px; border:0;';

  document.body.appendChild(frame);

  try {
    const doc = frame.contentDocument;
    const view = frame.contentWindow;
    if (!doc || !view) return false;

    doc.open();
    doc.write(html);
    doc.close();

    const selection = view.getSelection();
    if (!selection || !doc.body) return false;

    const range = doc.createRange();
    range.selectNodeContents(doc.body);
    selection.removeAllRanges();
    selection.addRange(range);

    // The copy applies to the focused document's selection, and this one is not it yet.
    view.focus();

    return doc.execCommand('copy');
  } catch {
    return false;
  } finally {
    frame.remove();

    // Focusing the frame moved it off the button; put it back so the keyboard stays where it was.
    if (returnFocusTo instanceof HTMLElement) {
      returnFocusTo.focus();
    }
  }
}

/**
 * A rough text rendering of the email, for the clipboard's plain-text flavour.
 *
 * Only the fallback route needs this — a selection copy produces its own plain text. Parsed into a
 * detached document rather than the live DOM: nothing is fetched, no script runs, and the preheader is
 * dropped along with everything else hidden, because it duplicates the opening line and reads as a
 * stutter in plain text.
 */
function toPlainText(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  for (const hidden of parsed.querySelectorAll('style, script, [style*="display:none"]')) {
    hidden.remove();
  }

  return (parsed.body?.textContent ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n');
}
